import { useEffect, useMemo, useState } from 'react';
import { JobDraft } from '@/redux/slices/slice_types';
import { JobBulkEditFormData } from '@/components/OrderPlacement/OrderPlacement.types';

type JobId = string;
type SelectedJobIds = Record<JobId, true>;

interface Props {
  jobs: Record<JobId, JobDraft>;
  filteredJobs: JobDraft[];
  selectedProfileOptions: unknown[];
  selectedCustomerOptions: unknown[];
  selectedTitleOptions: unknown[];
  handleUpdateJob(updatedJob: JobDraft): void;
  setHaveJobsBeenEdited(isEdited: boolean): void;
}

export const useJobBulkEdit = ({
  jobs,
  filteredJobs,
  selectedProfileOptions,
  selectedCustomerOptions,
  selectedTitleOptions,
  handleUpdateJob,
  setHaveJobsBeenEdited
}: Props) => {
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState<SelectedJobIds>({});
  const selectedJobs = useMemo(() => Object.values(jobs).filter((job) => selectedJobIds[job.id]), [jobs, selectedJobIds]);

  const isNoJobsSelected = Object.keys(selectedJobIds).length === 0;
  const isAllJobsSelected = useMemo(() => {
    return Object.values(jobs).every((job) => selectedJobIds[job.id]);
  }, [jobs, selectedJobIds]);
  const isSomeJobsSelected = !isNoJobsSelected && !isAllJobsSelected;

  const removeJobFromSelectedJobs = (jobId: JobId) => {
    if (selectedJobIds[jobId]) {
      delete selectedJobIds[jobId];
      setSelectedJobIds({ ...selectedJobIds });
    }
  };

  const handleBulkEditButtonClick = () => {
    setIsBulkEditModalOpen(true);
  };

  const handleBulkEditModalClose = () => {
    setIsBulkEditModalOpen(false);
  };

  const handleJobSelectionChange = (id: JobId) => (isSelected: boolean) => {
    if (isSelected) {
      setSelectedJobIds({
        ...selectedJobIds,
        [id]: true
      });
    } else {
      removeJobFromSelectedJobs(id);
    }
  };

  const selectAllJobs = () => {
    setSelectedJobIds(
      Object.values(filteredJobs).reduce((acc: SelectedJobIds, job) => {
        acc[job.id] = true;
        return acc;
      }, {})
    );
  };

  const deselectAllJobs = () => {
    setSelectedJobIds({});
  };

  const handleSelectAllCheckboxChange = () => {
    if (isAllJobsSelected) {
      deselectAllJobs();
    } else {
      selectAllJobs();
    }
  };

  const onBulkEditApply = (formData: JobBulkEditFormData) => {
    selectedJobs.forEach((job) => {
      handleUpdateJob({
        ...job,
        ...formData,
        profile: {
          ...job.profile,
          ...formData.profile,
          data: {
            ...job.profile?.data,
            ...formData.profile.data
          }
        }
      });
    });
    setHaveJobsBeenEdited(true);
  };

  useEffect(() => {
    deselectAllJobs();
  }, [selectedProfileOptions, selectedCustomerOptions, selectedTitleOptions]);

  return {
    isBulkEditModalOpen,
    selectedJobIds,
    selectedJobs,
    isNoJobsSelected,
    isSomeJobsSelected,
    isAllJobsSelected,
    removeJobFromSelectedJobs,
    handleSelectAllCheckboxChange,
    handleBulkEditButtonClick,
    handleJobSelectionChange,
    handleBulkEditModalClose,
    onBulkEditApply
  };
};
